import { API_URL, SITE } from "@api/constants";
import fetchStates from "@api/operations/fetch-states";
import State from "@api/types/state";
import { NextSeo } from "next-seo";
import { useEffect, useState } from "react";

import CenteredDialog from "@components/CenteredDialog";
import DialogButton from "@components/DialogButton";
import DialogFormField from "@components/DialogFormField";
import DialogFormSelect from "@components/DialogFormSelect";
import DialogWarning from "@components/DialogWarning";

type RegistrationResult = {
  token: string;
  reason: {
    key: string;
    value: string;
  };
  statusCode: number;
};

const RegisterDialog = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [address1, setAddress1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [role, setRole] = useState("");
  const [placeMostUsed, setPlaceMostUsed] = useState("");
  const [sourceInformed, setSourceInformed] = useState("");
  const [interestInOtherSportInjuryCourses, setInterestInOtherSportInjuryCourses] = useState("");
  const [error, setError] = useState("");

  const [states, setStates] = useState<State[]>([]);
  useEffect(() => {
    fetchStates("US").then((s) => {
      setStates(s);
    });
  }, []);

  const roles = [
    { label: "Athletic trainer", value: "athletic trainer" },
    { label: "First responder", value: "first responder" },
    { label: "Coach", value: "coach" },
    { label: "EMS", value: "EMS" },
    { label: "Parent", value: "parent" },
    { label: "Sideline volunteer", value: "sideline volunteer" },
    { label: "Medical professional", value: "medical professional" },
    { label: "Other", value: "other" },
  ];

  const sources = [
    { label: "NCHSAA advertisement", value: "NCHSAA advertisement" },
    { label: "Google", value: "Google" },
    { label: "MOFMC website", value: "MOFMC website" },
    { label: "Social media", value: "social media" },
    { label: "Word of mouth", value: "word of mouth" },
    { label: "Other", value: "other" },
  ];

  const validateAndSubmit = async () => {
    setError("");

    if (!firstName.length) {
      setError("First name is required.");
      return;
    }

    if (!lastName.length) {
      setError("Last name is required.");
      return;
    }

    if (!email.length) {
      setError("Email is required.");
      return;
    }

    if (!password.length) {
      setError("Password is required.");
      return;
    }

    if (password !== password2) {
      setError("Passwords must match.");
      return;
    }

    if (!address1.length) {
      setError("Address is required.");
      return;
    }

    if (!city.length) {
      setError("City is required.");
      return;
    }

    if (!state.length) {
      setError("State is required.");
      return;
    }

    if (!postalCode.length) {
      setError("Postal code is required.");
      return;
    }

    if (!role.length) {
      setError("Role/position is required.");
      return;
    }

    if (!placeMostUsed.length) {
      setError("Place most used is required.");
      return;
    }

    if (!sourceInformed.length) {
      setError("How did you hear about us is required.");
      return;
    }

    if (!interestInOtherSportInjuryCourses.length) {
      setError("Answer for interest in other sport injury courses is required.");
      return;
    }

    const headers = {
      "Content-Type": "application/json",
    };

    try {
      const result = await fetch(`${API_URL}/auth/register`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          site: SITE,
          identity: email,
          password,
          attributes: [
            { code: "firstName", value: firstName },
            { code: "lastName", value: lastName },
            { code: "address1", value: address1 },
            { code: "city", value: city },
            { code: "state", value: state },
            { code: "postalCode", value: postalCode },
            { code: "role", value: role },
            { code: "placeMostUsed", value: placeMostUsed },
            { code: "sourceInformed", value: sourceInformed },
            {
              code: "interestInOtherSportInjuryCourses",
              value: interestInOtherSportInjuryCourses,
            },
          ],
        }),
      });
      if (result.status === 200) {
        const json = (await result.json()) as RegistrationResult;
        window.location.href = `/token?token=${json.token}`;
      } else if (result.status === 409) {
        const json = (await result.json()) as RegistrationResult;
        setError(json.reason.value);
      } else {
        setError("An error occurred. Please try again later.");
      }
    } catch (e) {
      setError("An error occurred. Please try again later.");
    }
  };

  return (
    <>
      <CenteredDialog>
        <>
          <div className="action">
            {error ? <DialogWarning message={error} /> : null}
            <DialogFormField
              name="firstName"
              type="text"
              placeholder="First Name *"
              styles={{ width: "100%" }}
              onChange={(value) => {
                setFirstName(value);
              }}
            />
            <DialogFormField
              name="lastName"
              type="text"
              placeholder="Last Name *"
              styles={{ width: "100%" }}
              onChange={(value) => {
                setLastName(value);
              }}
            />
            <DialogFormField
              name="username"
              type="text"
              placeholder="Email Address"
              styles={{ width: "100%" }}
              onChange={(value) => {
                setEmail(value);
              }}
            />
            <DialogFormField
              name="password"
              type="password"
              placeholder="Password"
              styles={{ width: "100%" }}
              onChange={(value) => {
                setPassword(value);
              }}
            />
            <DialogFormField
              name="password"
              type="password"
              placeholder="Password (again)"
              styles={{ width: "100%" }}
              onChange={(value) => {
                setPassword2(value);
              }}
            />
            <DialogFormField
              name="address1"
              type="text"
              placeholder="Address"
              styles={{ width: "100%" }}
              onChange={(value) => {
                setAddress1(value);
              }}
            />
            <DialogFormField
              name="city"
              type="text"
              placeholder="City"
              styles={{ width: "100%" }}
              onChange={(value) => {
                setCity(value);
              }}
            />
            <DialogFormSelect
              name={"state"}
              options={states.map((s) => {
                return { label: s.name, value: s.code };
              })}
              defaultOption={{ label: "Select state...", value: "" }}
              styles={{ width: "100%" }}
              onChange={(value) => {
                setState(value);
              }}
            />
            <DialogFormField
              name="postalCode"
              type="text"
              placeholder="Postal Code"
              styles={{ width: "100%" }}
              onChange={(value) => {
                setPostalCode(value);
              }}
            />
            <DialogFormSelect
              name={"role"}
              label={"Role/Position"}
              options={roles}
              defaultOption={{ label: "Select...", value: "" }}
              styles={{ width: "100%" }}
              onChange={(value) => {
                setRole(value);
              }}
            />
            <DialogFormField
              name="placeMostUsed"
              type="text"
              label="Place where you will most often use these skills."
              styles={{ width: "100%" }}
              onChange={(value) => {
                setPlaceMostUsed(value);
              }}
            />
            <DialogFormSelect
              name={"sourceInformed"}
              label={"How did you hear about us?"}
              options={sources}
              defaultOption={{ label: "Select...", value: "" }}
              styles={{ width: "100%" }}
              onChange={(value) => {
                setSourceInformed(value);
              }}
            />
            <DialogFormSelect
              name={"interestInOtherSportInjuryCourses"}
              label={"Would you be interested in other sport injury-related courses?"}
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
              ]}
              defaultOption={{ label: "Select...", value: "" }}
              styles={{ width: "100%" }}
              onChange={(value) => {
                setInterestInOtherSportInjuryCourses(value);
              }}
            />
          </div>
          <div>
            <DialogButton onClick={() => validateAndSubmit()}>Create an Account</DialogButton>
          </div>
        </>
      </CenteredDialog>
    </>
  );
};

const RegisterPage = () => {
  return (
    <>
      <NextSeo title="Create an Account" />
      <RegisterDialog />
    </>
  );
};

export default RegisterPage;
